import React from 'react';
import { useTranslation } from 'react-i18next';

const BASE = 'https://api.getflashmultiservice.com';
const ENDPOINT = `${BASE}/api/v1/public/orders`;

const TEXT = {
  es: {
    title: 'API para webs propias',
    intro: 'Env\u00eda cada pedido de tu web a Flash Multiservice en el momento en que se produce. As\u00ed tus ventas, costes y ganancia real se actualizan solos, sin importar archivos.',
    stepsTitle: 'C\u00f3mo empezar',
    steps: [
      'Un administrador entra en Configuraci\u00f3n \u2192 "API para tu web propia" y crea una clave. La clave empieza por fms_live_ y solo se muestra una vez.',
      'Guarda la clave en tu servidor (por ejemplo, en una variable de entorno). Nunca la pongas en el JavaScript del navegador: cualquiera podr\u00eda verla.',
      'Cuando un pedido se pague en tu web, haz una petici\u00f3n POST al endpoint con los datos del pedido.',
    ],
    authTitle: 'Autenticaci\u00f3n',
    authText: 'Env\u00eda la clave en la cabecera Authorization: Bearer fms_live_... (tambi\u00e9n se acepta X-API-Key). Cada clave solo puede escribir en su propia empresa.',
    fieldsTitle: 'Campos del pedido (JSON)',
    colField: 'Campo',
    colType: 'Tipo',
    colDesc: 'Descripci\u00f3n',
    required: 'obligatorio',
    fields: [
      ['order_id', 'texto', 'N\u00famero o identificador del pedido en tu web. Es \u00fanico: si lo env\u00edas dos veces no se duplica.', true],
      ['total', 'n\u00famero', 'Total cobrado al cliente, con impuestos y env\u00edo incluidos.', true],
      ['status', 'texto', 'pending, paid, shipped o cancelled. Por defecto paid. Solo los pagados y enviados cuentan en la ganancia real.', false],
      ['items', 'lista', 'Art\u00edculos del pedido: sku (obligatorio), name, quantity y unit_price. El coste de producto se toma del cat\u00e1logo por SKU.', false],
      ['shipping', 'n\u00famero', 'Coste de env\u00edo que pagas t\u00fa. Por defecto 0.', false],
      ['fees', 'n\u00famero', 'Comisi\u00f3n de la pasarela de pago (Stripe, PayPal, Redsys...). Por defecto 0.', false],
      ['tax', 'n\u00famero', 'IVA real del pedido. Si no lo env\u00edas, se estima con el tipo de IVA de tu empresa.', false],
      ['currency', 'texto', 'C\u00f3digo de moneda (EUR, USD...). Debe ser la moneda de tu empresa. Por defecto, la de tu empresa.', false],
      ['created_at', 'texto', 'Fecha del pedido en formato ISO 8601 (ej. 2026-10-07T10:30:00Z). Por defecto, ahora.', false],
      ['channel', 'texto', 'Nombre del canal tal como quieres verlo en la app. Por defecto "web".', false],
      ['customer', 'objeto', 'Datos del cliente: name y email.', false],
    ],
    behaviorTitle: 'Comportamiento',
    behavior: [
      'Si env\u00edas otra vez el mismo order_id con otro status (por ejemplo, cancelled), se actualiza el estado. Si es igual, se ignora y no se duplica.',
      'Si un SKU no existe en tu cat\u00e1logo, se crea el producto con coste 0. Compl\u00e9talo en Productos para que la ganancia sea correcta.',
      'El stock de cada producto se descuenta al recibir el pedido.',
      'L\u00edmite: 120 peticiones por minuto por clave.',
    ],
    responsesTitle: 'Respuestas',
    responses: [
      ['200 \u00b7 created', 'Pedido creado. Devuelve order_id.'],
      ['200 \u00b7 duplicate', 'El pedido ya exist\u00eda; no se ha creado otro.'],
      ['200 \u00b7 updated', 'El pedido ya exist\u00eda y se ha cambiado su estado.'],
      ['401', 'Falta la clave, no es v\u00e1lida o est\u00e1 revocada.'],
      ['422', 'Alg\u00fan campo no es v\u00e1lido. El mensaje en "detail" indica cu\u00e1l.'],
      ['429', 'Demasiadas peticiones. Espera un minuto.'],
    ],
    examplesTitle: 'Ejemplos',
    security: 'Si crees que una clave se ha filtrado, rev\u00f3cala en Configuraci\u00f3n y crea otra. Las peticiones con la clave revocada dejan de funcionar al instante.',
  },
  en: {
    title: 'API for custom websites',
    intro: 'Send every order from your website to Flash Multiservice as it happens. Your sales, costs and real profit update automatically, with no file imports.',
    stepsTitle: 'Getting started',
    steps: [
      'An admin goes to Settings \u2192 "API for your own website" and creates a key. Keys start with fms_live_ and are shown only once.',
      'Store the key on your server (for example, in an environment variable). Never put it in browser JavaScript: anyone could see it.',
      'When an order is paid on your website, send a POST request to the endpoint with the order data.',
    ],
    authTitle: 'Authentication',
    authText: 'Send the key in the Authorization: Bearer fms_live_... header (X-API-Key is also accepted). Each key can only write to its own company.',
    fieldsTitle: 'Order fields (JSON)',
    colField: 'Field',
    colType: 'Type',
    colDesc: 'Description',
    required: 'required',
    fields: [
      ['order_id', 'text', 'Order number or identifier on your website. It is unique: sending it twice does not duplicate it.', true],
      ['total', 'number', 'Total charged to the customer, including taxes and shipping.', true],
      ['status', 'text', 'pending, paid, shipped or cancelled. Defaults to paid. Only paid and shipped orders count towards real profit.', false],
      ['items', 'list', 'Order items: sku (required), name, quantity and unit_price. Product cost is taken from the catalog by SKU.', false],
      ['shipping', 'number', 'Shipping cost you pay. Defaults to 0.', false],
      ['fees', 'number', 'Payment gateway fee (Stripe, PayPal...). Defaults to 0.', false],
      ['tax', 'number', 'Actual VAT/tax of the order. If omitted, it is estimated with your company VAT rate.', false],
      ['currency', 'text', 'Currency code (EUR, USD...). Must be your company currency. Defaults to your company currency.', false],
      ['created_at', 'text', 'Order date in ISO 8601 (e.g. 2026-10-07T10:30:00Z). Defaults to now.', false],
      ['channel', 'text', 'Channel name as you want to see it in the app. Defaults to "web".', false],
      ['customer', 'object', 'Customer data: name and email.', false],
    ],
    behaviorTitle: 'Behavior',
    behavior: [
      'If you send the same order_id again with a different status (e.g. cancelled), the status is updated. If it is the same, it is ignored and not duplicated.',
      'If a SKU does not exist in your catalog, the product is created with cost 0. Complete it in Products so profit is correct.',
      'Each product stock is reduced when the order is received.',
      'Limit: 120 requests per minute per key.',
    ],
    responsesTitle: 'Responses',
    responses: [
      ['200 \u00b7 created', 'Order created. Returns order_id.'],
      ['200 \u00b7 duplicate', 'The order already existed; no new one was created.'],
      ['200 \u00b7 updated', 'The order already existed and its status was changed.'],
      ['401', 'The key is missing, invalid or revoked.'],
      ['422', 'A field is invalid. The "detail" message says which one.'],
      ['429', 'Too many requests. Wait a minute.'],
    ],
    examplesTitle: 'Examples',
    security: 'If you think a key has leaked, revoke it in Settings and create a new one. Requests with the revoked key stop working immediately.',
  },
  fr: {
    title: 'API pour sites sur mesure',
    intro: 'Envoyez chaque commande de votre site \u00e0 Flash Multiservice au moment o\u00f9 elle a lieu. Vos ventes, co\u00fbts et b\u00e9n\u00e9fice r\u00e9el se mettent \u00e0 jour seuls, sans importer de fichiers.',
    stepsTitle: 'Pour commencer',
    steps: [
      'Un administrateur va dans Param\u00e8tres \u2192 \u00ab API pour votre propre site \u00bb et cr\u00e9e une cl\u00e9. Les cl\u00e9s commencent par fms_live_ et ne sont affich\u00e9es qu\u2019une seule fois.',
      'Conservez la cl\u00e9 sur votre serveur (par exemple dans une variable d\u2019environnement). Ne la mettez jamais dans le JavaScript du navigateur : tout le monde pourrait la voir.',
      'Quand une commande est pay\u00e9e sur votre site, envoyez une requ\u00eate POST \u00e0 l\u2019endpoint avec les donn\u00e9es de la commande.',
    ],
    authTitle: 'Authentification',
    authText: 'Envoyez la cl\u00e9 dans l\u2019en-t\u00eate Authorization: Bearer fms_live_... (X-API-Key est aussi accept\u00e9). Chaque cl\u00e9 ne peut \u00e9crire que dans sa propre entreprise.',
    fieldsTitle: 'Champs de la commande (JSON)',
    colField: 'Champ',
    colType: 'Type',
    colDesc: 'Description',
    required: 'obligatoire',
    fields: [
      ['order_id', 'texte', 'Num\u00e9ro ou identifiant de la commande sur votre site. Il est unique : l\u2019envoyer deux fois ne cr\u00e9e pas de doublon.', true],
      ['total', 'nombre', 'Total pay\u00e9 par le client, taxes et livraison comprises.', true],
      ['status', 'texte', 'pending, paid, shipped ou cancelled. Par d\u00e9faut paid. Seules les commandes pay\u00e9es et exp\u00e9di\u00e9es comptent dans le b\u00e9n\u00e9fice r\u00e9el.', false],
      ['items', 'liste', 'Articles : sku (obligatoire), name, quantity et unit_price. Le co\u00fbt produit est pris du catalogue par SKU.', false],
      ['shipping', 'nombre', 'Frais de livraison que vous payez. Par d\u00e9faut 0.', false],
      ['fees', 'nombre', 'Commission de la passerelle de paiement (Stripe, PayPal...). Par d\u00e9faut 0.', false],
      ['tax', 'nombre', 'TVA r\u00e9elle de la commande. Si elle est absente, elle est estim\u00e9e avec le taux de votre entreprise.', false],
      ['currency', 'texte', 'Code de devise (EUR, USD...). Doit \u00eatre la devise de votre entreprise. Par d\u00e9faut, celle de votre entreprise.', false],
      ['created_at', 'texte', 'Date de la commande au format ISO 8601 (ex. 2026-10-07T10:30:00Z). Par d\u00e9faut, maintenant.', false],
      ['channel', 'texte', 'Nom du canal tel que vous voulez le voir dans l\u2019application. Par d\u00e9faut \u00ab web \u00bb.', false],
      ['customer', 'objet', 'Donn\u00e9es du client : name et email.', false],
    ],
    behaviorTitle: 'Fonctionnement',
    behavior: [
      'Si vous renvoyez le m\u00eame order_id avec un autre status (ex. cancelled), le statut est mis \u00e0 jour. S\u2019il est identique, il est ignor\u00e9, sans doublon.',
      'Si un SKU n\u2019existe pas dans votre catalogue, le produit est cr\u00e9\u00e9 avec un co\u00fbt de 0. Compl\u00e9tez-le dans Produits pour que le b\u00e9n\u00e9fice soit correct.',
      'Le stock de chaque produit est d\u00e9compt\u00e9 \u00e0 la r\u00e9ception de la commande.',
      'Limite : 120 requ\u00eates par minute et par cl\u00e9.',
    ],
    responsesTitle: 'R\u00e9ponses',
    responses: [
      ['200 \u00b7 created', 'Commande cr\u00e9\u00e9e. Renvoie order_id.'],
      ['200 \u00b7 duplicate', 'La commande existait d\u00e9j\u00e0 ; aucune autre n\u2019a \u00e9t\u00e9 cr\u00e9\u00e9e.'],
      ['200 \u00b7 updated', 'La commande existait d\u00e9j\u00e0 et son statut a \u00e9t\u00e9 modifi\u00e9.'],
      ['401', 'Cl\u00e9 absente, invalide ou r\u00e9voqu\u00e9e.'],
      ['422', 'Un champ n\u2019est pas valide. Le message \u00ab detail \u00bb indique lequel.'],
      ['429', 'Trop de requ\u00eates. Attendez une minute.'],
    ],
    examplesTitle: 'Exemples',
    security: 'Si vous pensez qu\u2019une cl\u00e9 a fuit\u00e9, r\u00e9voquez-la dans Param\u00e8tres et cr\u00e9ez-en une autre. Les requ\u00eates avec la cl\u00e9 r\u00e9voqu\u00e9e cessent de fonctionner imm\u00e9diatement.',
  },
};

const SAMPLE_JSON = `{
  "order_id": "1001",
  "status": "paid",
  "total": 59.90,
  "shipping": 4.95,
  "fees": 1.20,
  "created_at": "2026-10-07T10:30:00Z",
  "customer": { "name": "Ana Garc\u00eda", "email": "ana@example.com" },
  "items": [
    { "sku": "CAM-001", "name": "Camiseta negra", "quantity": 2, "unit_price": 19.95 },
    { "sku": "GOR-002", "name": "Gorra", "quantity": 1, "unit_price": 15.00 }
  ]
}`;

const CURL = `curl -X POST ${ENDPOINT} \\
  -H "Authorization: Bearer $FLASH_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"order_id":"1001","status":"paid","total":59.90,
       "items":[{"sku":"CAM-001","quantity":2,"unit_price":19.95}]}'`;

const PHP = `<?php
$order = [
  'order_id' => '1001',
  'status'   => 'paid',
  'total'    => 59.90,
  'shipping' => 4.95,
  'items'    => [
    ['sku' => 'CAM-001', 'quantity' => 2, 'unit_price' => 19.95],
  ],
];

$ch = curl_init('${ENDPOINT}');
curl_setopt_array($ch, [
  CURLOPT_POST => true,
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER => [
    'Authorization: Bearer ' . getenv('FLASH_API_KEY'),
    'Content-Type: application/json',
  ],
  CURLOPT_POSTFIELDS => json_encode($order),
]);
$response = curl_exec($ch);
curl_close($ch);`;

const NODE = `// Node.js 18+ (en tu servidor, nunca en el navegador)
const response = await fetch('${ENDPOINT}', {
  method: 'POST',
  headers: {
    Authorization: 'Bearer ' + process.env.FLASH_API_KEY,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    order_id: '1001',
    status: 'paid',
    total: 59.9,
    items: [{ sku: 'CAM-001', quantity: 2, unit_price: 19.95 }],
  }),
});
const result = await response.json(); // { status: 'created', order_id: '...' }`;

const getText = (lang) => TEXT[(lang || 'es').slice(0, 2)] || TEXT.es;

function Code({ children }) {
  return (
    <pre className="bg-surfaceAlt border border-edge rounded-lg p-4 text-[12px] text-white overflow-x-auto whitespace-pre mb-4">
      <code>{children}</code>
    </pre>
  );
}

function ApiDocs() {
  const { i18n } = useTranslation();
  const tx = getText(i18n.language);
  const h2 = 'text-white text-lg font-semibold mt-10 mb-3';
  const p = 'text-[#aaa] text-sm leading-relaxed';

  return (
    <div className="bg-panel min-h-screen font-sans px-4 py-10">
      <div className="max-w-3xl mx-auto">
        <a href="/" className="text-accent text-sm font-bold">Flash Multiservice</a>
        <h1 className="text-white text-3xl font-bold mt-4 mb-3">{tx.title}</h1>
        <p className={p}>{tx.intro}</p>

        <h2 className={h2}>{tx.stepsTitle}</h2>
        <ol className="list-decimal pl-5 flex flex-col gap-2">
          {tx.steps.map((step) => <li key={step} className={p}>{step}</li>)}
        </ol>

        <h2 className={h2}>Endpoint</h2>
        <Code>{`POST ${ENDPOINT}`}</Code>

        <h2 className={h2}>{tx.authTitle}</h2>
        <p className={`${p} mb-3`}>{tx.authText}</p>
        <Code>{'Authorization: Bearer fms_live_xxxxxxxxxxxxxxxx\nContent-Type: application/json'}</Code>

        <h2 className={h2}>{tx.fieldsTitle}</h2>
        <div className="overflow-x-auto mb-4">
          <table className="w-full min-w-[560px] border-collapse">
            <thead>
              <tr className="border-b border-edge">
                {[tx.colField, tx.colType, tx.colDesc].map((h) => (
                  <th key={h} className="text-muted text-xs text-left pb-2 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tx.fields.map(([field, type, desc, req]) => (
                <tr key={field} className="border-b border-surfaceAlt align-top">
                  <td className="py-2 pr-3 text-white text-sm font-mono whitespace-nowrap">
                    {field}
                    {req && <span className="block text-accent text-[10px] font-sans">{tx.required}</span>}
                  </td>
                  <td className="py-2 pr-3 text-muted text-xs whitespace-nowrap">{type}</td>
                  <td className="py-2 text-[#aaa] text-xs leading-relaxed">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Code>{SAMPLE_JSON}</Code>

        <h2 className={h2}>{tx.behaviorTitle}</h2>
        <ul className="list-disc pl-5 flex flex-col gap-2">
          {tx.behavior.map((item) => <li key={item} className={p}>{item}</li>)}
        </ul>

        <h2 className={h2}>{tx.responsesTitle}</h2>
        <div className="flex flex-col gap-1 mb-2">
          {tx.responses.map(([code, desc]) => (
            <p key={code} className={p}><span className="text-white font-mono">{code}</span>{' \u2014 '}{desc}</p>
          ))}
        </div>
        <Code>{'{ "status": "created", "order_id": "8f3c...", "items": 2 }'}</Code>

        <h2 className={h2}>{tx.examplesTitle}</h2>
        <p className="text-muted text-xs mb-2">cURL</p>
        <Code>{CURL}</Code>
        <p className="text-muted text-xs mb-2">PHP</p>
        <Code>{PHP}</Code>
        <p className="text-muted text-xs mb-2">Node.js</p>
        <Code>{NODE}</Code>

        <div className="bg-[#2a1f0f] border border-[#7f5f1d] rounded-lg p-4 mt-6">
          <p className="text-amber-400 text-sm">{tx.security}</p>
        </div>
      </div>
    </div>
  );
}

export default ApiDocs;
